import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const TeacherStudents = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        // Fetch teacher profile first
        const profileResponse = await api.get(`/teachers/user/${user?._id}`);
        
        // Fetch students in teacher's classes
        const studentsResponse = await api.get(`/students/teacher/${profileResponse.data.data._id}`);
        
        // Process student data to match expected format
        const processedStudents = studentsResponse.data.data.map(student => ({
          id: student._id,
          name: student.userId?.name || student.name,
          rollNumber: student.rollNumber,
          className: student.class,
          section: student.section,
          phone: student.phone,
          email: student.userId?.email || student.email,
          attendance: student.attendancePercentage || 'N/A'
        }));
        
        setStudents(processedStudents);
      } catch (error) {
        console.error('Error fetching students:', error);
        // Fallback to mock data if API fails
        const mockStudents = [
          {
            id: 1,
            name: 'John Smith',
            rollNumber: '10A001',
            className: 'Grade 10',
            section: 'A',
            phone: '+1 (555) 123-4567',
            email: 'john.smith@example.com',
            attendance: '85%'
          },
          {
            id: 2,
            name: 'Emma Johnson',
            rollNumber: '10A002',
            className: 'Grade 10',
            section: 'A',
            phone: '+1 (555) 234-5678',
            email: 'emma.johnson@example.com',
            attendance: '92%'
          },
          {
            id: 3,
            name: 'Michael Brown',
            rollNumber: '10A003',
            className: 'Grade 10',
            section: 'A',
            phone: '+1 (555) 345-6789',
            email: 'michael.brown@example.com',
            attendance: '78%'
          },
          {
            id: 4,
            name: 'Sarah Davis',
            rollNumber: '10B001',
            className: 'Grade 10',
            section: 'B',
            phone: '+1 (555) 456-7890',
            email: 'sarah.davis@example.com',
            attendance: '88%'
          },
          {
            id: 5,
            name: 'David Wilson',
            rollNumber: '10B002',
            className: 'Grade 10',
            section: 'B',
            phone: '+1 (555) 567-8901',
            email: 'david.wilson@example.com',
            attendance: '95%'
          }
        ];
        setStudents(mockStudents);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchStudents();
    }
  }, [user]);

  const filteredStudents = selectedClass 
    ? students.filter(student => student.className === selectedClass)
    : students;

  const uniqueClasses = [...new Set(students.map(student => student.className))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">My Students</h1>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Classes</option>
            {uniqueClasses.map(className => (
              <option key={className} value={className}>{className}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roll Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attendance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.rollNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{student.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.className}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.section}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        parseFloat(student.attendance) >= 80 ? 'bg-green-100 text-green-800' :
                        parseFloat(student.attendance) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {student.attendance}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-primary hover:text-blue-700 mr-4">Attendance</button>
                      <button className="text-green-600 hover:text-green-800 mr-4">Marks</button>
                      <button className="text-gray-600 hover:text-gray-800">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Attendance Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Poor Attendance (&lt;60%)</span>
                <span className="font-medium text-red-600">
                  {students.filter(s => parseFloat(s.attendance) < 60).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Average Attendance (60-79%)</span>
                <span className="font-medium text-yellow-600">
                  {students.filter(s => parseFloat(s.attendance) >= 60 && parseFloat(s.attendance) < 80).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Good Attendance (&gt;=80%)</span>
                <span className="font-medium text-green-600">
                  {students.filter(s => parseFloat(s.attendance) >= 80).length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Class Distribution</h3>
            <div className="space-y-3">
              {uniqueClasses.map(className => (
                <div key={className} className="flex justify-between">
                  <span>{className}</span>
                  <span className="font-medium">
                    {students.filter(s => s.className === className).length} students
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-blue-700 transition text-sm">
                Take Attendance
              </button>
              <button className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition text-sm">
                Enter Marks
              </button>
              <button className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 transition text-sm">
                Upload Materials
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherStudents;